import { useState, useRef, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

const navItems = [
  { path: '/create', label: 'Create' },
  { path: '/queue', label: 'Queue' },
  { path: '/accounts', label: 'Accounts' },
]

function DotsIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
    </svg>
  )
}

export function Nav() {
  const { logout } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm transition-colors ${
      isActive
        ? 'text-primary-text font-medium'
        : 'text-secondary-text hover:text-primary-text'
    }`

  return (
    <nav className="border-b border-border bg-primary-bg">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-8">
            <NavLink to="/" className="text-lg font-medium">
              Beacon
            </NavLink>
            <div className="flex items-center gap-6">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={linkClass}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Desktop: inline links */}
          <div className="hidden md:flex items-center gap-4">
            <NavLink to="/settings" className={linkClass}>
              Settings
            </NavLink>
            <button
              onClick={logout}
              className="text-sm text-secondary-text hover:text-primary-text transition-colors"
            >
              Logout
            </button>
          </div>

          {/* Mobile: three-dot menu */}
          <div className="relative md:hidden" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="p-1 text-secondary-text hover:text-primary-text transition-colors"
              aria-label="Menu"
            >
              <DotsIcon />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-40 rounded-md border border-border bg-primary-bg shadow-lg py-1 z-50">
                <NavLink
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? 'text-primary-text font-medium'
                        : 'text-secondary-text hover:text-primary-text'
                    }`
                  }
                >
                  Settings
                </NavLink>
                <button
                  onClick={() => { setMenuOpen(false); logout() }}
                  className="block w-full text-left px-4 py-2 text-sm text-secondary-text hover:text-primary-text transition-colors"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}
